import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../apiBase';
import { formatDateTime } from '../utils/dateFormat';
import ConsentAcceptPanel from './ConsentAcceptPanel';

export default function ConsentStatusWidget() {
    const { t } = useTranslation(['profile', 'common']);
    const navigate = useNavigate();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [withdrawing, setWithdrawing] = useState(false);
    const [error, setError] = useState('');

    const token = localStorage.getItem('token');
    const api = axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${token}` }
    });

    useEffect(() => {
        load();
    }, []);

    async function load() {
        setLoading(true);
        setError('');
        try {
            const res = await api.get('/consent/my-status');
            setStatus(res.data);
        } catch {
            setError(t('profile:consentLoadError'));
        }
        setLoading(false);
    }

    async function confirmWithdraw() {
        setWithdrawing(true);
        setError('');
        try {
            await api.post('/consent/withdraw');
            localStorage.removeItem('token');
            localStorage.removeItem('role');
            localStorage.removeItem('activeSchool');
            setShowModal(false);
            navigate('/login', {
                replace: true,
                state: { message: t('profile:consentWithdrawnAccess') }
            });
        } catch (err) {
            setError(err.response?.data?.message || t('profile:consentWithdrawFailed'));
        }
        setWithdrawing(false);
    }

    if (loading) return <p style={{ color: '#888', fontSize: '14px' }}>{t('common:loading')}</p>;

    const givenAt = status?.consentGivenAt
        ? formatDateTime(status.consentGivenAt)
        : '—';

    const needsAccept = !status?.consentGiven && !status?.consentWithdrawnAt;

    return (
        <div style={{ marginBottom: '20px' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>{t('profile:personalData')}</h3>

            {needsAccept ? (
                <>
                    <p
                        style={{
                            fontSize: '14px',
                            color: '#5b21b6',
                            margin: '0 0 16px',
                            padding: '12px 14px',
                            background: '#f3eeff',
                            borderRadius: '10px',
                            lineHeight: 1.5
                        }}
                    >
                        {t('profile:consentLegacyHint')}
                    </p>
                    <ConsentAcceptPanel onAccepted={load} />
                </>
            ) : (
                <div
                    style={{
                        background: '#fff',
                        borderRadius: '14px',
                        padding: '20px',
                        border: '1px solid var(--sp-gray-100)'
                    }}
                >
                    {status?.consentWithdrawnAt ? (
                        <p style={{ color: '#c62828', fontSize: '14px' }}>
                            {t('profile:consentWithdrawnAt', {
                                date: formatDateTime(status.consentWithdrawnAt)
                            })}
                        </p>
                    ) : (
                        <>
                            <p style={{ fontSize: '14px', color: '#2e7d32', margin: '0 0 8px' }}>
                                {t('profile:consentGivenPd', { date: givenAt })}
                            </p>
                            <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 16px' }}>
                                {t('profile:policyVersion', {
                                    version: status.consentVersion || '—'
                                })}
                            </p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                <a
                                    href="/privacy-policy"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        padding: '10px 16px',
                                        borderRadius: '8px',
                                        border: '1px solid var(--sp-gray-200)',
                                        textDecoration: 'none',
                                        color: '#333',
                                        fontSize: '14px'
                                    }}
                                >
                                    {t('profile:openPolicy')}
                                </a>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(true)}
                                    style={{
                                        padding: '10px 16px',
                                        borderRadius: '8px',
                                        border: '1px solid #ef9a9a',
                                        background: '#fff',
                                        color: '#c62828',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                    }}
                                >
                                    {t('profile:withdrawConsent')}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {error && <p style={{ color: '#c62828', fontSize: '13px', marginTop: '12px' }}>{error}</p>}

            {showModal && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.45)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999
                    }}
                >
                    <div
                        style={{
                            background: '#fff',
                            borderRadius: '14px',
                            padding: '24px',
                            maxWidth: '420px',
                            margin: '20px'
                        }}
                    >
                        <h4 style={{ margin: '0 0 12px' }}>{t('profile:withdrawTitle')}</h4>
                        <p style={{ fontSize: '14px', color: '#555', lineHeight: 1.5 }}>
                            {t('profile:withdrawMessage')}
                        </p>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd',
                                    background: '#fff',
                                    cursor: 'pointer'
                                }}
                            >
                                {t('common:cancel')}
                            </button>
                            <button
                                type="button"
                                onClick={confirmWithdraw}
                                disabled={withdrawing}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    background: '#c62828',
                                    color: '#fff',
                                    cursor: 'pointer'
                                }}
                            >
                                {withdrawing ? '…' : t('profile:confirmWithdraw')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
